import { z } from "zod";

/**
 * Amazon-side availability is separate from AdPraxis implementation readiness.
 * `unavailable` is intentionally retryable/unknown; it must never be presented
 * as proof that a Profile does not support Sponsored Display.
 */
export const SponsoredDisplayCapabilityStateSchema = z.enum([
  "supported",
  "unsupported",
  "unavailable",
]);
export type SponsoredDisplayCapabilityState = z.infer<typeof SponsoredDisplayCapabilityStateSchema>;

export const SponsoredDisplayCapabilitySourceSchema = z.enum([
  "runtime_probe",
  "documented_contract",
  "product_scope",
]);

export const SponsoredDisplayCapabilityReasonSchema = z.enum([
  "access_verified",
  "profile_not_eligible",
  "authorization_invalid",
  "amazon_temporarily_unavailable",
  "probe_rejected",
  "implementation_pending",
  "not_in_scope",
]);
export type SponsoredDisplayCapabilityReason = z.infer<
  typeof SponsoredDisplayCapabilityReasonSchema
>;

const SupportedCapabilitySchema = z
  .object({
    state: z.literal("supported"),
    source: z.enum(["runtime_probe", "documented_contract"]),
    reason: z.literal("access_verified"),
  })
  .strict();

const ProfileUnsupportedCapabilitySchema = z
  .object({
    state: z.literal("unsupported"),
    source: z.literal("runtime_probe"),
    reason: z.literal("profile_not_eligible"),
  })
  .strict();

const OutOfScopeCapabilitySchema = z
  .object({
    state: z.literal("unsupported"),
    source: z.literal("product_scope"),
    reason: z.literal("not_in_scope"),
  })
  .strict();

const RuntimeUnavailableCapabilitySchema = z
  .object({
    state: z.literal("unavailable"),
    source: z.literal("runtime_probe"),
    reason: z.enum(["authorization_invalid", "amazon_temporarily_unavailable", "probe_rejected"]),
  })
  .strict();

const ImplementationPendingCapabilitySchema = z
  .object({
    state: z.literal("unavailable"),
    source: z.literal("product_scope"),
    reason: z.literal("implementation_pending"),
  })
  .strict();

/** State, source and reason are one atomic contract; impossible triples fail closed. */
export const SponsoredDisplayFeatureCapabilitySchema = z.union([
  SupportedCapabilitySchema,
  ProfileUnsupportedCapabilitySchema,
  OutOfScopeCapabilitySchema,
  RuntimeUnavailableCapabilitySchema,
  ImplementationPendingCapabilitySchema,
]);
export type SponsoredDisplayFeatureCapability = z.infer<
  typeof SponsoredDisplayFeatureCapabilitySchema
>;

function optimizationCapabilitySchema(costType: "cpc" | "vcpm") {
  const costTypeSchema = z.literal(costType);
  return z.union([
    SupportedCapabilitySchema.extend({ costType: costTypeSchema }),
    ProfileUnsupportedCapabilitySchema.extend({ costType: costTypeSchema }),
    OutOfScopeCapabilitySchema.extend({ costType: costTypeSchema }),
    RuntimeUnavailableCapabilitySchema.extend({ costType: costTypeSchema }),
    ImplementationPendingCapabilitySchema.extend({ costType: costTypeSchema }),
  ]);
}

const SponsoredDisplayOutOfScopeCapabilitySchema = OutOfScopeCapabilitySchema;

/**
 * Read-only, Profile-scoped SD capability matrix (ADP-129).
 *
 * This is not a submit schema. ADP-130 owns the later SD creation payload.
 * Callers must gate the Campaign type on `campaignCreation`, while individual
 * controls use their corresponding feature entry.
 */
export const SponsoredDisplayCapabilitiesSchema = z.object({
  contractVersion: z.literal("2026-08-20"),
  adProduct: z.literal("sd"),
  marketplaceId: z.string().min(1),
  marketplaceName: z.string().min(1),
  profileId: z.string().min(1),
  amazonAccess: SponsoredDisplayFeatureCapabilitySchema,
  campaignCreation: SponsoredDisplayFeatureCapabilitySchema,
  targeting: z.object({
    contextual: SponsoredDisplayFeatureCapabilitySchema,
    amazonAudience: SponsoredDisplayFeatureCapabilitySchema,
    viewsRemarketing: SponsoredDisplayFeatureCapabilitySchema,
    purchasesRemarketing: SponsoredDisplayFeatureCapabilitySchema,
    amcAudience: SponsoredDisplayOutOfScopeCapabilitySchema,
  }),
  optimization: z.object({
    pageVisits: optimizationCapabilitySchema("cpc"),
    conversions: optimizationCapabilitySchema("cpc"),
    viewableImpressions: optimizationCapabilitySchema("vcpm"),
  }),
  creative: z.object({
    dynamic: SponsoredDisplayFeatureCapabilitySchema,
    logo: SponsoredDisplayFeatureCapabilitySchema,
    headline: SponsoredDisplayFeatureCapabilitySchema,
    image: SponsoredDisplayFeatureCapabilitySchema,
  }),
  tactics: z.object({
    recommended: z.literal("T00030"),
    legacyContextual: z.literal("T00020"),
  }),
  limits: z.object({
    maxProducts: z.literal(500),
    headlineMaxLength: z.literal(50),
    logoMaxBytes: z.literal(1_000_000),
    imageMaxBytes: z.literal(5_000_000),
  }),
  probe: z.object({
    method: z.literal("GET"),
    path: z.literal("/sd/campaigns"),
    readOnly: z.literal(true),
    checkedAt: z.string().datetime(),
    amazonRequestId: z.string().nullable(),
  }),
});
export type SponsoredDisplayCapabilities = z.infer<typeof SponsoredDisplayCapabilitiesSchema>;
